/**
 * OPERA OS Android push foundation (Capacitor + FCM).
 *
 * Sprint 1: permission + registration + listeners + safe tap navigation.
 * Sprint 2: persist FCM token via authenticated device-token API (session + CSRF).
 * Sprint 3: foreground refresh of Notification Center on push receive; tap deep-link.
 * Server-side FCM send lives in PHP (HTTP v1) — never in Vite/Android credentials.
 * Does NOT clear cookies / storage / OPERA_SESSID.
 */

import { Capacitor } from "@capacitor/core";
import { PushNotifications } from "@capacitor/push-notifications";
import { authFetch, fetchSession, operaApiHeaders } from "@/modules/auth/api/authApi";

export const OPERA_OPS_CHANNEL_ID = "opera_ops";

let started = false;
let listenersAttached = false;
/** In-memory FCM token for refresh + logout revoke (never persisted to localStorage). */
let currentFcmToken: string | null = null;
let persistInFlight: Promise<void> | null = null;

function isDev(): boolean {
  return Boolean(import.meta.env.DEV);
}

/** True when running inside the Capacitor native shell (Android/iOS). */
function isNativeShell(): boolean {
  return Capacitor.isNativePlatform();
}

/** Log diagnostics on native + in Vite dev — never cookies, CSRF, or passwords. */
function pushLog(message: string, detail?: Record<string, unknown>): void {
  if (!isDev() && !isNativeShell()) return;
  if (detail) {
    console.info(`[opera-push] ${message}`, detail);
  } else {
    console.info(`[opera-push] ${message}`);
  }
}

function maskToken(token: string): string {
  const t = token.trim();
  if (t.length <= 12) return "(short-token)";
  return `${t.slice(0, 6)}…${t.slice(-4)} (len=${t.length})`;
}

function publishDebugRegistration(detail: Record<string, unknown>): void {
  if (!isDev() && !isNativeShell()) return;
  (window as unknown as { __OPERA_PUSH_REG?: Record<string, unknown> }).__OPERA_PUSH_REG = {
    ...detail,
    at: new Date().toISOString(),
  };
}

/**
 * Allow only OPERA SPA deep links:
 * - relative app paths: /foo
 * - absolute https://app.cyberwebopera.com/...
 * Reject arbitrary external hosts (never open inside WebView).
 */
export function resolveSafeOperaActionUrl(raw: unknown): string | null {
  if (typeof raw !== "string") return null;
  const value = raw.trim();
  if (!value) return null;

  if (value.startsWith("/") && !value.startsWith("//")) {
    if (value.includes("://")) return null;
    return value;
  }

  try {
    const url = new URL(value);
    if (url.protocol !== "https:") return null;
    if (url.hostname.toLowerCase() !== "app.cyberwebopera.com") return null;
    return `${url.pathname}${url.search}${url.hash}` || "/";
  } catch {
    return null;
  }
}

function navigateSafely(actionUrl: unknown): void {
  const path = resolveSafeOperaActionUrl(actionUrl);
  if (!path) {
    pushLog("tap ignored — action_url not an OPERA SPA path");
    return;
  }
  pushLog("navigating to safe OPERA path", { path });
  // Same WebView origin (server.url = app.cyberwebopera.com) — no cookie wipe.
  window.location.assign(path);
}

async function ensureOpsChannel(): Promise<void> {
  if (Capacitor.getPlatform() !== "android") return;
  try {
    await PushNotifications.createChannel({
      id: OPERA_OPS_CHANNEL_ID,
      name: "OPERA OS",
      description: "OPERA OS operational notifications",
      importance: 3,
      visibility: 1,
      vibration: true,
    });
  } catch (err) {
    pushLog("createChannel skipped/failed (non-fatal)", {
      reason: err instanceof Error ? err.message : "unknown",
    });
  }
}

/**
 * POST token to device-token.php using existing session + CSRF.
 * Safe to repeat; never throws to callers in a way that breaks OPERA OS.
 */
async function persistDeviceToken(token: string): Promise<void> {
  const trimmed = token.trim();
  if (!trimmed) return;

  if (persistInFlight) {
    await persistInFlight;
  }

  persistInFlight = (async () => {
    try {
      // Ensure CSRF is fresh before privileged POST (same pattern as authApi).
      await fetchSession();
      const res = await authFetch("/api/notifications/device-token.php", {
        method: "POST",
        headers: operaApiHeaders(true),
        body: JSON.stringify({
          token: trimmed,
          platform: "android",
          device_label: typeof navigator !== "undefined" ? navigator.userAgent.slice(0, 120) : "android",
          app_version: "",
        }),
      });

      let data: {
        ok?: boolean;
        success?: boolean;
        userId?: string;
        platform?: string;
        lastSeenAt?: string | null;
        tokenMasked?: string;
        error?: string;
        deviceToken?: { created?: boolean; reassigned?: boolean };
      } = {};
      try {
        data = (await res.json()) as typeof data;
      } catch {
        data = {};
      }

      if (!res.ok || !data.ok) {
        pushLog("device-token API failed (non-fatal)", {
          status: res.status,
          error: data.error || "unknown",
        });
        publishDebugRegistration({
          ok: false,
          status: res.status,
          error: data.error || "unknown",
          token: maskToken(trimmed),
        });
        return;
      }

      pushLog("device-token registered", {
        userId: data.userId,
        platform: data.platform,
        lastSeenAt: data.lastSeenAt,
        token: data.tokenMasked || maskToken(trimmed),
        created: data.deviceToken?.created,
        reassigned: data.deviceToken?.reassigned,
      });
      publishDebugRegistration({
        ok: true,
        userId: data.userId,
        platform: data.platform,
        lastSeenAt: data.lastSeenAt,
        token: data.tokenMasked || maskToken(trimmed),
        created: data.deviceToken?.created,
        reassigned: data.deviceToken?.reassigned,
      });
    } catch (err) {
      pushLog("device-token persist error (non-fatal)", {
        reason: err instanceof Error ? err.message : "unknown",
      });
      publishDebugRegistration({
        ok: false,
        error: err instanceof Error ? err.message : "unknown",
        token: maskToken(trimmed),
      });
    }
  })();

  try {
    await persistInFlight;
  } finally {
    persistInFlight = null;
  }
}

async function attachListeners(): Promise<void> {
  if (listenersAttached) return;
  listenersAttached = true;

  await PushNotifications.addListener("registration", (token) => {
    const value = String(token?.value ?? "").trim();
    pushLog("FCM registration success", { token: maskToken(value) });
    if (isDev()) {
      (window as unknown as { __OPERA_PUSH_TOKEN_MASKED?: string }).__OPERA_PUSH_TOKEN_MASKED =
        maskToken(value);
    }
    if (!value) return;
    currentFcmToken = value;
    // Token refresh + first registration both land here.
    void persistDeviceToken(value);
  });

  await PushNotifications.addListener("registrationError", (error) => {
    pushLog("FCM registration error (non-fatal)", {
      error: String(error?.error ?? "unknown"),
    });
  });

  await PushNotifications.addListener("pushNotificationReceived", (notification) => {
    // Foreground: Capacitor may already present via presentationOptions.
    // Do not fabricate a second tray item — refresh in-app Notification Center only.
    const data =
      notification.data && typeof notification.data === "object"
        ? (notification.data as Record<string, unknown>)
        : undefined;
    pushLog("foreground notification", {
      id: notification.id,
      title: notification.title,
      notificationId: typeof data?.notification_id === "string" ? data.notification_id : undefined,
      type: typeof data?.type === "string" ? data.type : undefined,
      hasActionUrl: Boolean(data?.action_url),
    });
    void import("@/store/appStore")
      .then(({ useAppStore }) => {
        if (useAppStore.getState().isAuthenticated) {
          void useAppStore.getState().loadNotifications();
        }
      })
      .catch(() => {
        /* non-fatal */
      });
  });

  await PushNotifications.addListener("pushNotificationActionPerformed", (action) => {
    const data = action.notification?.data as Record<string, unknown> | undefined;
    pushLog("notification action/tap", {
      actionId: action.actionId,
      notificationId: typeof data?.notification_id === "string" ? data.notification_id : undefined,
      type: typeof data?.type === "string" ? data.type : undefined,
      hasActionUrl: Boolean(data?.action_url),
    });
    // Never bypass auth — SPA router will send unauthenticated users to login.
    navigateSafely(data?.action_url);
  });
}

/**
 * Soft-revoke the current device token for the authenticated user.
 * Must run BEFORE session logout. Never clears cookies/storage. Non-fatal.
 */
export async function revokeOperaDeviceTokenOnLogout(): Promise<void> {
  const token = currentFcmToken;
  // Allow push start again after next login.
  started = false;

  if (!token || !Capacitor.isNativePlatform()) {
    currentFcmToken = null;
    return;
  }

  try {
    await fetchSession();
    const res = await authFetch("/api/notifications/device-token-revoke.php", {
      method: "POST",
      headers: operaApiHeaders(true),
      body: JSON.stringify({ token }),
    });
    pushLog("device-token revoke attempted", {
      status: res.status,
      token: maskToken(token),
    });
  } catch (err) {
    pushLog("device-token revoke failed (non-fatal)", {
      reason: err instanceof Error ? err.message : "unknown",
    });
  } finally {
    currentFcmToken = null;
  }
}

/**
 * Start push registration after the user is authenticated.
 * Safe to call multiple times; non-fatal if permission denied or Firebase missing.
 */
export async function startOperaPushNotifications(): Promise<void> {
  if (!Capacitor.isNativePlatform()) {
    return;
  }
  if (Capacitor.getPlatform() !== "android") {
    return;
  }
  if (started) {
    // Already running this session — if we still hold a token, refresh upsert is fine.
    if (currentFcmToken) {
      void persistDeviceToken(currentFcmToken);
    }
    return;
  }
  started = true;

  try {
    await attachListeners();
    await ensureOpsChannel();

    const current = await PushNotifications.checkPermissions();
    pushLog("permission status", { receive: current.receive });

    let receive = current.receive;
    if (receive === "prompt" || receive === "prompt-with-rationale") {
      const requested = await PushNotifications.requestPermissions();
      receive = requested.receive;
      pushLog("permission after request", { receive });
    }

    if (receive !== "granted") {
      pushLog("permission not granted — OPERA OS continues without push");
      return;
    }

    await PushNotifications.register();
    pushLog("PushNotifications.register() called");
  } catch (err) {
    // Never block OPERA OS on push failures (missing google-services.json, etc.).
    pushLog("push init failed (non-fatal)", {
      reason: err instanceof Error ? err.message : "unknown",
    });
  }
}
